import React, { useState, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { uploadImageAsync, fileToBase64, compressImage } from '../utils/imageUpload';

interface ProfilePhotoUploadProps {
  currentPhotoUrl?: string;
  onPhotoChange: (photoUrl: string) => void;
  onPhotoUpload?: (photoUrl: string) => void;
  userId: string;
  disabled?: boolean;
  className?: string;
}

const ProfilePhotoUpload: React.FC<ProfilePhotoUploadProps> = ({
  currentPhotoUrl,
  onPhotoChange,
  onPhotoUpload,
  userId,
  disabled = false,
  className = ''
}) => {
  const { t } = useTranslation();
  const [isUploading, setIsUploading] = useState(false);
  const [previewUrl, setPreviewUrl] = useState<string | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const cameraInputRef = useRef<HTMLInputElement>(null);

  const handleFileSelect = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) return;

    try {
      setIsUploading(true);

      // 이미지 압축
      const compressedFile = await compressImage(file, 800, 0.8);
      
      // 미리보기용 Base64 URL 생성
      const base64Url = await fileToBase64(compressedFile);
      setPreviewUrl(base64Url);
      onPhotoChange(base64Url);

      // Firebase Storage에 업로드
      const uploadedUrl = await uploadImageAsync(compressedFile, userId);
      
      // 실제 URL로 업데이트
      setPreviewUrl(uploadedUrl);
      onPhotoChange(uploadedUrl);
      
      // 부모 컴포넌트에 업로드 완료 알림
      if (onPhotoUpload) {
        onPhotoUpload(uploadedUrl);
      }
    } catch (error) {
      console.error('Profile photo upload error:', error);
alert(t('profilePhoto.uploadError', 'Failed to upload profile photo.'));
    } finally {
      setIsUploading(false);
    }
  };

  const handleGalleryClick = () => {
    fileInputRef.current?.click();
  };

  const handleCameraClick = () => {
    cameraInputRef.current?.click();
  };

  const handleRemovePhoto = () => {
    setPreviewUrl(null);
    onPhotoChange('');
    if (fileInputRef.current) fileInputRef.current.value = '';
    if (cameraInputRef.current) cameraInputRef.current.value = '';
  };

  const displayPhotoUrl = previewUrl || currentPhotoUrl;

  return (
    <div className={`profile-photo-upload ${className}`}>
      {/* 숨겨진 파일 입력 */}
      <input
        ref={fileInputRef}
        type="file"
        accept="image/*"
        onChange={handleFileSelect}
        className="hidden"
        disabled={disabled || isUploading}
      />
      
      {/* 카메라 입력 (웹에서는 파일 선택으로 대체) */}
      <input
        ref={cameraInputRef}
        type="file"
        accept="image/*"
        capture="environment"
        onChange={handleFileSelect}
        className="hidden"
        disabled={disabled || isUploading}
      />

      {/* 프로필 사진 미리보기 */}
      <div className="flex flex-col items-center space-y-4">
        <div className="relative">
          <div className="w-32 h-32 rounded-full overflow-hidden border-4 border-gray-200 bg-gray-100 flex items-center justify-center">
            {displayPhotoUrl ? (
              <img
                src={displayPhotoUrl}
                alt={t('profilePhoto.alt', 'Profile Photo')}
                className="w-full h-full object-cover"
              />
            ) : (
              <div className="text-gray-400 text-4xl">
                <svg className="w-16 h-16" fill="currentColor" viewBox="0 0 20 20">
                  <path fillRule="evenodd" d="M10 9a3 3 0 100-6 3 3 0 000 6zm-7 9a7 7 0 1114 0H3z" clipRule="evenodd" />
                </svg>
              </div>
            )}
            
            {isUploading && (
              <div className="absolute inset-0 bg-black bg-opacity-50 flex items-center justify-center rounded-full">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-white"></div>
              </div>
            )}
          </div>
          
          {displayPhotoUrl && (
            <button
              onClick={handleRemovePhoto}
              disabled={disabled || isUploading}
              className="absolute -top-2 -right-2 bg-slate-500 text-white rounded-full p-1 hover:bg-slate-600 disabled:opacity-50"
              title={t('profilePhoto.removePhoto', 'Remove Photo')}
            >
              <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 20 20">
                <path fillRule="evenodd" d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z" clipRule="evenodd" />
              </svg>
            </button>
          )}
        </div>

        {/* 업로드 버튼들 */}
        {!displayPhotoUrl && (
          <div className="flex space-x-3">
            <button
              onClick={handleGalleryClick}
              disabled={disabled || isUploading}
              className="lux-ink flex items-center space-x-2 px-4 py-2 text-white rounded-full hover:opacity-90 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 20 20">
                <path fillRule="evenodd" d="M4 3a2 2 0 00-2 2v10a2 2 0 002 2h12a2 2 0 002-2V5a2 2 0 00-2-2H4zm12 12H4l4-8 3 6 2-4 3 6z" clipRule="evenodd" />
              </svg>
              <span>{t('profilePhoto.gallery', 'Gallery')}</span>
            </button>
            
            <button
              onClick={handleCameraClick}
              disabled={disabled || isUploading}
              className="lux-btn-outline flex items-center space-x-2 px-4 py-2 rounded-full disabled:opacity-50 disabled:cursor-not-allowed"
            >
              <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 20 20">
                <path fillRule="evenodd" d="M4 5a2 2 0 00-2 2v8a2 2 0 002 2h12a2 2 0 002-2V7a2 2 0 00-2-2h-1.586l-1.707-1.707A1 1 0 0012.414 3H7.586a1 1 0 00-.707.293L5.293 5H4zm6 9a3 3 0 100-6 3 3 0 000 6z" clipRule="evenodd" />
              </svg>
              <span>{t('profilePhoto.camera', 'Camera')}</span>
            </button>
          </div>
        )}

        {/* 사진 변경 버튼 */}
        {displayPhotoUrl && (
          <div className="flex space-x-3">
            <button
              onClick={handleGalleryClick}
              disabled={disabled || isUploading}
              className="flex items-center space-x-2 px-3 py-1.5 bg-gray-600 text-white rounded-lg hover:bg-gray-700 disabled:opacity-50 disabled:cursor-not-allowed text-sm"
            >
              <svg className="w-3 h-3" fill="currentColor" viewBox="0 0 20 20">
                <path fillRule="evenodd" d="M4 3a2 2 0 00-2 2v10a2 2 0 002 2h12a2 2 0 002-2V5a2 2 0 00-2-2H4zm12 12H4l4-8 3 6 2-4 3 6z" clipRule="evenodd" />
              </svg>
              <span>{t('common.edit', 'Edit')}</span>
            </button>
          </div>
        )}

        {/* 업로드 상태 표시 */}
        {isUploading && (
          <div className="text-sm text-gray-600">
            {t('profilePhoto.uploading', 'Uploading...')}
          </div>
        )}
      </div>
    </div>
  );
};

export default ProfilePhotoUpload;
